/**
 * 配對分析結果頁面
 * 顯示配對分析的詳細結果
 */

import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Divider,
  Chip,
  Alert,
  Tabs,
  Tab,
  Box,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CompareArrows as CompareArrowsIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  Favorite as FavoriteIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  EmojiEvents as EmojiEventsIcon,
} from '@mui/icons-material';
import { PairResponse, getScoreColor, getScoreGrade } from '../api/pair';
import { useCurrency } from '../contexts/CurrencyContext';
import { formatBirthTime } from '../api/chart';
import ReportContent from '../components/ReportContent';
import ShareCardModal, { extractSummary } from '../components/ShareCardModal';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Legend, Tooltip
} from 'recharts';

const PairResultPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();

  // 從路由 state 取得配對結果
  const result: PairResponse = location.state?.result;
  const isMultiPair = location.state?.isMultiPair || false;
  const results: Array<{ partner: string; result: PairResponse }> = location.state?.results || [];
  const personAName = location.state?.personAName || '本人';

  // 多人配對：當前選擇的 Tab（0=總覽排名，1+=個別夥伴）
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // 取得當前顯示的結果（多人模式 tab 0 為總覽，tab 1+ 為個別夥伴）
  const currentPartnerIndex = isMultiPair ? currentTabIndex - 1 : 0;
  const isShowingSummary = isMultiPair && currentTabIndex === 0;
  const currentResult = isMultiPair && results.length > 0 && currentPartnerIndex >= 0
    ? results[currentPartnerIndex].result
    : (!isMultiPair ? result : (results.length > 0 ? results[0].result : result));

  /**
   * 從配對結果中提取綜合配對分數
   * 優先使用結構化欄位，否則從 output 文字中解析
   */
  const extractScore = (pairResult: PairResponse): number => {
    // 方法1：compatibility_score（標準配對分析）
    if (pairResult?.result?.compatibility_score !== undefined) {
      return pairResult.result.compatibility_score;
    }
    // 方法1b：best_score（多人配對 PAIR026-034）
    if (pairResult?.result?.best_score !== undefined) {
      return pairResult.result.best_score;
    }
    // 方法1c：overall_score（AnalyzeMatching 直接結果）
    if (pairResult?.result?.overall_score !== undefined) {
      return pairResult.result.overall_score;
    }
    // 方法1d：scores 陣列（多人配對分析）
    if (pairResult?.result?.scores !== undefined) {
      const scores = pairResult.result.scores;
      if (Array.isArray(scores) && scores.length > 0) {
        return scores[0];
      } else if (typeof scores === 'number') {
        return scores;
      }
    }
    // 方法2：從 output 文字中解析
    if (pairResult?.output) {
      const patterns = [
        /整體配對度[：:]\s*([\d.]+)/,
        /整體配對度:\s*([\d.]+)%/,
        /配對指數:\s*([\d.]+)%/,
        /綜合配對度[：:]\s*([\d.]+)/,
        /推薦：.*?配對分數[：:]\s*([\d.]+)/,
        /Overall.*?:\s*([\d.]+)%/i,
      ];
      for (const pattern of patterns) {
        const match = pairResult.output.match(pattern);
        if (match) {
          return parseFloat(match[1]);
        }
      }
    }
    return 0;
  };

  /**
   * 從配對報告中解析宮位或特質評分（用於雷達圖）
   * 例如：事業宮配對分數: 85.2、命宮配對分數: 75.0 等
   */
  const parseComparisonData = (results: Array<{ partner: string; result: PairResponse }>) => {
    const categories = ['命宮', '事業宮', '財帛宮', '夫妻宮', '交友宮'];
    // 使用更明顯區分的顏色配置（4種以上也能清楚辨識）
    const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#a28dff', '#00c49f', '#ffbb28', '#ff8042'];

    // 準備雷達圖數據結構
    const radarData = categories.map(category => {
      const dataPoint: any = { subject: category.replace('宮', '') };

      // 為每個夥伴提取該宮位的分數
      results.forEach((item, index) => {
        const regex = new RegExp(`${category}[】】配對分數]*[：:｜|\\s]+([-\\d.]+)`, 'i');
        const match = item.result.output?.match(regex);
        if (match) {
          dataPoint[item.partner] = parseFloat(match[1]);
        } else {
          dataPoint[item.partner] = 0;
        }
      });

      return dataPoint;
    });

    // 過濾掉所有人都是 0 分的軸線
    const filteredRadarData = radarData.filter(point => {
      // 檢查是否所有夥伴的分數都是 0
      const hasNonZeroScore = results.some(item => {
        const score = point[item.partner];
        return typeof score === 'number' && score > 0;
      });
      return hasNonZeroScore;
    });

    // 檢查是否有有效數據
    const hasValidData = filteredRadarData.length > 0 && filteredRadarData.some(point =>
      Object.values(point).some(val => typeof val === 'number' && val > 0)
    );

    if (!hasValidData) return null;

    // 動態計算數據範圍（用於設定雷達圖刻度）
    let minValue = 0;
    let maxValue = 0;

    filteredRadarData.forEach(point => {
      Object.entries(point).forEach(([key, value]) => {
        if (key !== 'subject' && typeof value === 'number') {
          minValue = Math.min(minValue, value);
          maxValue = Math.max(maxValue, value);
        }
      });
    });

    // 根據數據範圍智慧判斷刻度類型
    let domain: [number, number];
    let tickCount = 5;

    if (maxValue <= 5 && minValue >= 0) {
      // 五大人格特質範圍 (0-5)
      domain = [0, 5];
      tickCount = 6;
    } else if (maxValue <= 15 && minValue >= -15) {
      // 宮值範圍 (-15 到 15)
      domain = [-15, 15];
      tickCount = 7;
    } else if (maxValue <= 100 && minValue >= 0) {
      // 百分比或配對分數 (0-100)
      domain = [0, 100];
      tickCount = 5;
    } else {
      // 自動範圍（向上取整到最近的 10）
      const roundedMin = Math.floor(minValue / 10) * 10;
      const roundedMax = Math.ceil(maxValue / 10) * 10;
      domain = [roundedMin, roundedMax];
      tickCount = Math.min(Math.abs(roundedMax - roundedMin) / 10 + 1, 10);
    }

    return { radarData: filteredRadarData, colors, domain, tickCount };
  };

  // 多人配對排名資料
  const rankedResults = isMultiPair && results.length > 0
    ? [...results]
        .map((item, originalIndex) => ({
          ...item,
          originalIndex,
          score: extractScore(item.result),
        }))
        .sort((a, b) => b.score - a.score)
    : [];

  // 解析雷達圖數據
  const comparisonChartData = isMultiPair && results.length >= 2
    ? parseComparisonData(results)
    : null;

  // 如果沒有結果資料，顯示錯誤訊息
  if ((!result || !result.success) && (!isMultiPair || results.length === 0)) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          無法取得配對分析結果，請重新操作。
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/pair')}
          sx={{ mt: 2 }}
        >
          返回配對分析
        </Button>
      </Container>
    );
  }

  /**
   * 評分進度條組件
   */
  const ScoreBar: React.FC<{ label: string; score: number }> = ({ label, score }) => {
    const safeScore = typeof score === 'number' ? score : 0;
    const color = getScoreColor(safeScore);
    const grade = getScoreGrade(safeScore);

    return (
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <Typography variant="body2" fontWeight="bold">
            {String(label)}
          </Typography>
          <Typography variant="body2" fontWeight="bold" style={{ color }}>
            {String(safeScore)} 分（{String(grade)}）
          </Typography>
        </div>
        <LinearProgress
          variant="determinate"
          value={safeScore}
          sx={{
            height: 10,
            borderRadius: 5,
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              backgroundColor: color,
              borderRadius: 5,
            },
          }}
        />
      </div>
    );
  };

  /**
   * 處理列印
   */
  const handlePrint = () => {
    window.print();
  };

  /**
   * 處理分享
   */
  const handleShare = () => {
    setShareModalOpen(true);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* 頁面標題 */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Typography variant="h4" gutterBottom>
          <CompareArrowsIcon sx={{ fontSize: 40, verticalAlign: 'middle', mr: 1 }} />
          配對分析結果
        </Typography>
        <Typography variant="body1">
          {String(currentResult?.question_text || '綜合配對分析')}
        </Typography>
        {isMultiPair && (
          <Chip
            label={`工作夥伴配對：共 ${results.length} 位`}
            sx={{ mt: 2, backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
          />
        )}
        {currentResult?.created_at && (
          <Chip
            label={`查詢時間：${String(new Date(currentResult.created_at).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' }))}`}
            sx={{ mt: 2, ml: isMultiPair ? 1 : 0, backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
          />
        )}
      </Paper>

      {/* 多人配對：夥伴切換 Tab（含總覽排名） */}
      {isMultiPair && results.length > 0 && (
        <Paper elevation={2} sx={{ mb: 3 }}>
          <Tabs
            value={currentTabIndex}
            onChange={(e, newValue) => setCurrentTabIndex(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              icon={<EmojiEventsIcon />}
              iconPosition="start"
              label="總覽排名"
              sx={{ fontWeight: currentTabIndex === 0 ? 'bold' : 'normal' }}
            />
            {results.map((item, index) => (
              <Tab
                key={index}
                label={`${String(item.partner)}`}
                sx={{ fontWeight: currentTabIndex === index + 1 ? 'bold' : 'normal' }}
              />
            ))}
          </Tabs>
          {!isShowingSummary && currentPartnerIndex >= 0 && currentPartnerIndex < results.length && (
            <Box sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="body2" color="text.secondary">
                {String(personAName)} 與 {String(results[currentPartnerIndex].partner)} 的配對分析
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* 多人配對：總覽排名面板 */}
      {isShowingSummary && rankedResults.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            <EmojiEventsIcon sx={{ verticalAlign: 'middle', mr: 1, color: '#ffc107' }} />
            夥伴適配度排名
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            以下依照綜合配對分數由高到低排列，點擊可查看個別詳細分析報告
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {rankedResults.map((item, rankIndex) => {
            const medal = rankIndex === 0 ? '🥇' : rankIndex === 1 ? '🥈' : rankIndex === 2 ? '🥉' : `${rankIndex + 1}.`;
            const scoreColor = getScoreColor(item.score);
            const scoreGrade = getScoreGrade(item.score);

            return (
              <Card
                key={item.originalIndex}
                elevation={rankIndex === 0 ? 4 : 1}
                sx={{
                  mb: 2,
                  cursor: 'pointer',
                  border: rankIndex === 0 ? '2px solid #ffc107' : '1px solid #e0e0e0',
                  '&:hover': { boxShadow: 4, backgroundColor: '#f5f5f5' },
                  transition: 'all 0.2s',
                }}
                onClick={() => setCurrentTabIndex(item.originalIndex + 1)}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h5" sx={{ minWidth: 40 }}>
                      {medal}
                    </Typography>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {String(item.partner)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {String(personAName)} 與 {String(item.partner)} 的配對分析
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: scoreColor }}>
                      {Number(item.score).toFixed(1)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: scoreColor }}>
                      {scoreGrade}
                    </Typography>
                  </Box>
                </CardContent>
                {/* 分數進度條 */}
                <LinearProgress
                  variant="determinate"
                  value={item.score}
                  sx={{
                    height: 6,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: scoreColor,
                    },
                  }}
                />
              </Card>
            );
          })}

          {/* 多人配對視覺化雷達圖 */}
          {comparisonChartData && comparisonChartData.radarData.length > 0 && (
            <Box sx={{ mt: 4, mb: 3 }}>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon sx={{ mr: 1, color: '#667eea' }} />
                宮位配對度比較圖
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                視覺化比較各夥伴在不同宮位的配對程度（分數越高越契合）
              </Typography>

              <ResponsiveContainer width="100%" height={400}>
                {/* @ts-ignore - recharts TypeScript compatibility */}
                <RadarChart data={comparisonChartData.radarData}>
                  <PolarGrid stroke="#ccc" />
                  {/* @ts-ignore */}
                  <PolarAngleAxis
                    dataKey="subject"
                    tick={{ fill: '#666', fontSize: 12 }}
                  />
                  {/* @ts-ignore */}
                  <PolarRadiusAxis
                    angle={90}
                    domain={comparisonChartData.domain || [0, 100]}
                    tick={{ fontSize: 10 }}
                    tickCount={comparisonChartData.tickCount || 5}
                  />

                  {/* 為每個夥伴繪製一條雷達線 */}
                  {results.map((item, index) => (
                    /* @ts-ignore */
                    <Radar
                      key={index}
                      name={item.partner}
                      dataKey={item.partner}
                      stroke={comparisonChartData.colors[index % comparisonChartData.colors.length]}
                      fill={comparisonChartData.colors[index % comparisonChartData.colors.length]}
                      fillOpacity={0.3}
                    />
                  ))}

                  {/* @ts-ignore */}
                  <Tooltip
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc' }}
                    formatter={(value: any) => `${value.toFixed(1)} 分`}
                  />
                  {/* @ts-ignore */}
                  <Legend />
                </RadarChart>
              </ResponsiveContainer>
            </Box>
          )}

          {/* 排名說明 */}
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>排名說明：</strong>綜合配對分數由紫微斗數 12 宮位分析計算，分數越高代表與您的工作適配度越佳。
              {rankedResults.length >= 2 && (
                <>
                  <br />
                  第一名 <strong>{String(rankedResults[0].partner)}</strong>（{Number(rankedResults[0].score).toFixed(1)} 分）
                  與最後一名 <strong>{String(rankedResults[rankedResults.length - 1].partner)}</strong>（{Number(rankedResults[rankedResults.length - 1].score).toFixed(1)} 分）
                  相差 <strong>{(rankedResults[0].score - rankedResults[rankedResults.length - 1].score).toFixed(1)}</strong> 分。
                </>
              )}
            </Typography>
          </Alert>
        </Paper>
      )}

      {/* 操作按鈕 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/pair')}
          >
            返回配對分析
          </Button>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            列印報告
          </Button>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<ShareIcon />}
            onClick={handleShare}
          >
            分享結果
          </Button>
        </Grid>
      </Grid>

      {/* 個別配對詳細內容（非總覽排名時顯示） */}
      {!isShowingSummary && currentResult && (currentResult.person_a_info || currentResult.person_b_info) && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {currentResult.person_a_info && (
            <Grid item xs={12} sm={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {isMultiPair ? String(personAName) : 'Person A'}
                  </Typography>
                  <Typography variant="body2">
                    姓名：{String(currentResult.person_a_info.name || '未提供')}
                  </Typography>
                  <Typography variant="body2">
                    性別：{currentResult.person_a_info.gender === 'Male' ? '男' : '女'}
                  </Typography>
                  <Typography variant="body2">
                    出生時間：{formatBirthTime(currentResult.person_a_info.birth_time)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
          {currentResult.person_b_info && (
            <Grid item xs={12} sm={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {isMultiPair ? String(results[currentPartnerIndex].partner) : 'Person B'}
                  </Typography>
                  <Typography variant="body2">
                    姓名：{String(currentResult.person_b_info.name || '未提供')}
                  </Typography>
                  <Typography variant="body2">
                    性別：{currentResult.person_b_info.gender === 'Male' ? '男' : '女'}
                  </Typography>
                  <Typography variant="body2">
                    出生時間：{formatBirthTime(currentResult.person_b_info.birth_time)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* 配對評分 */}
      {!isShowingSummary && currentResult?.result && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            <FavoriteIcon sx={{ verticalAlign: 'middle', mr: 1, color: '#e91e63' }} />
            配對評分
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Alert severity="info" icon={false} sx={{ mb: 2, py: 0.5, fontSize: '0.8rem' }}>
            分數說明（0–100）：80+ 絕配・65+ 良配・50+ 普通・35+ 不佳・35 以下 不合
          </Alert>

          {/* 綜合配對度 */}
          {currentResult.result.compatibility_score !== undefined && (
            <div style={{ marginBottom: '24px' }}>
              <Typography variant="h6" gutterBottom>
                綜合配對度
              </Typography>
              <ScoreBar label="整體配對評分" score={currentResult.result.compatibility_score} />
            </div>
          )}

          {/* 五大維度評分 */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            細項評分
          </Typography>
          {currentResult.result.personality_match !== undefined && (
            <ScoreBar label="個性契合度" score={currentResult.result.personality_match} />
          )}
          {currentResult.result.communication_match !== undefined && (
            <ScoreBar label="溝通協調度" score={currentResult.result.communication_match} />
          )}
          {currentResult.result.values_match !== undefined && (
            <ScoreBar label="價值觀一致性" score={currentResult.result.values_match} />
          )}
          {currentResult.result.harmony_score !== undefined && (
            <ScoreBar label="相處融洽度" score={currentResult.result.harmony_score} />
          )}
          {currentResult.result.stability_score !== undefined && (
            <ScoreBar label="長期穩定性" score={currentResult.result.stability_score} />
          )}
        </Paper>
      )}

      {/* 優勢與劣勢 */}
      {!isShowingSummary && currentResult?.result && (currentResult.result.strengths || currentResult.result.weaknesses) && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* 優勢特質 */}
          {currentResult.result.strengths && Array.isArray(currentResult.result.strengths) && currentResult.result.strengths.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <TrendingUpIcon sx={{ verticalAlign: 'middle', mr: 1, color: '#4caf50' }} />
                    優勢特質
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {currentResult.result.strengths
                    .filter(s => s != null)
                    .map((strength, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                        • {String(strength)}
                      </Typography>
                    ))}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* 需注意事項 */}
          {currentResult.result.weaknesses && Array.isArray(currentResult.result.weaknesses) && currentResult.result.weaknesses.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <WarningIcon sx={{ verticalAlign: 'middle', mr: 1, color: '#ff9800' }} />
                    需注意事項
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {currentResult.result.weaknesses
                    .filter(w => w != null)
                    .map((weakness, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                        • {String(weakness)}
                      </Typography>
                    ))}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* 互補建議 */}
      {!isShowingSummary && currentResult?.result?.suggestions && Array.isArray(currentResult.result.suggestions) && currentResult.result.suggestions.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            💡 互補建議
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {currentResult.result.suggestions
            .filter(s => s != null)
            .map((suggestion, index) => (
              <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                {index + 1}. {String(suggestion)}
              </Typography>
            ))}
        </Paper>
      )}

      {/* 詳細分析文字 */}
      {!isShowingSummary && currentResult?.output && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            📋 詳細分析
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <ReportContent text={String(currentResult.output || '')} />
        </Paper>
      )}

      {/* 費用與餘額資訊 */}
      {!isShowingSummary && currentResult && (currentResult.cost !== undefined || currentResult.balance_after !== undefined) && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            💳 交易資訊
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {isMultiPair && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              總共分析：<strong>{results.length} 位夥伴</strong>（以次計費）
            </Typography>
          )}
          {currentResult.cost !== undefined && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              本次費用：<strong>{String(formatPrice(currentResult.cost))}</strong>
            </Typography>
          )}
          {currentResult.balance_before !== undefined && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              查詢前餘額：{String(formatPrice(currentResult.balance_before))}
            </Typography>
          )}
          {currentResult.balance_after !== undefined && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              查詢後餘額：<strong>{String(formatPrice(currentResult.balance_after))}</strong>
            </Typography>
          )}
          {currentResult.query_id !== undefined && (
            <Typography variant="body2" color="text.secondary">
              查詢編號：{String(currentResult.query_id)}
            </Typography>
          )}
        </Paper>
      )}

      {/* 返回按鈕 */}
      <div style={{ textAlign: 'center' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/pair')}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            py: 1.5,
            px: 6,
          }}
        >
          返回配對分析
        </Button>
      </div>

      {/* 分享卡片 Modal */}
      {currentResult && (() => {
        const pairScore = extractScore(currentResult);
        const gradeMap: Record<string, string> = {
          '優': '非常契合', '良': '相當契合', '中': '尚可', '差': '需要磨合', '極差': '差異較大',
        };
        const gradeShort = pairScore > 0 ? getScoreGrade(pairScore) : undefined;
        const gradeText = gradeShort ? (gradeMap[gradeShort] ?? gradeShort) : undefined;
        return (
          <ShareCardModal
            open={shareModalOpen}
            onClose={() => setShareModalOpen(false)}
            questionText={currentResult.question_text || '配對分析'}
            summary={extractSummary(currentResult.output || '')}
            category="配對"
            score={pairScore > 0 ? pairScore : undefined}
            grade={gradeText}
          />
        );
      })()}
    </Container>
  );
};

export default PairResultPage;
